import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";

interface TrainingCategory {
  id: string;
  name_en: string;
  name_el: string;
  description_en?: string;
  description_el?: string;
  color: string;
  icon: string;
}

interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  category_id?: string;
  training_categories?: TrainingCategory;
  category?: string;
  content_en?: any;
  content_el?: any;
  description_en?: string;
  description_el?: string;
  duration_minutes?: number;
  difficulty_level?: string;
  is_active?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  tags?: string[];
}

interface ModuleFormProps {
  module: TrainingModule | null;
  categories: TrainingCategory[];
  onSubmit: () => void;
  onCancel: () => void;
  onChange: (module: TrainingModule) => void;
}

export const ModuleForm = ({ 
  module, 
  categories, 
  onSubmit, 
  onCancel,
  onChange
}: ModuleFormProps) => {
  const { language } = useLanguage();

  if (!module) return null;

  const updateModule = (updates: Partial<TrainingModule>) => {
    onChange({ ...module, ...updates });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="module_title_en">Title (English)</Label>
          <Input
            id="module_title_en"
            value={module.title_en}
            onChange={(e) => updateModule({ title_en: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="module_title_el">Title (Greek)</Label>
          <Input
            id="module_title_el"
            value={module.title_el}
            onChange={(e) => updateModule({ title_el: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="module_desc_en">Description (English)</Label>
          <Textarea
            id="module_desc_en"
            value={module.description_en || ''}
            onChange={(e) => updateModule({ description_en: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="module_desc_el">Description (Greek)</Label>
          <Textarea
            id="module_desc_el"
            value={module.description_el || ''}
            onChange={(e) => updateModule({ description_el: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select 
            value={module.category_id || ''} 
            onValueChange={(value) => updateModule({ category_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {language === 'en' ? category.name_en : category.name_el}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="duration">Duration (minutes)</Label>
          <Input
            id="duration"
            type="number"
            value={module.duration_minutes || 30}
            onChange={(e) => updateModule({ duration_minutes: parseInt(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="difficulty">Difficulty</Label>
          <Select 
            value={module.difficulty_level || 'beginner'} 
            onValueChange={(value) => updateModule({ difficulty_level: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="content_en">Content (English)</Label>
          <Textarea
            id="content_en"
            className="min-h-[100px]"
            value={module.content_en || ''}
            onChange={(e) => updateModule({ content_en: e.target.value })}
            placeholder="Enter training content in English..."
          />
        </div>
        <div>
          <Label htmlFor="content_el">Content (Greek)</Label>
          <Textarea
            id="content_el"
            className="min-h-[100px]"
            value={module.content_el || ''}
            onChange={(e) => updateModule({ content_el: e.target.value })}
            placeholder="Enter training content in Greek..."
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit}>
          Save Module
        </Button>
      </div>
    </div>
  );
};